import { Category } from '@fcom/dapi/api/models/category';
import { ServiceCatalogService } from '@fcom/dapi/interfaces/services.interface';
import { getLoungeContentFragmentKey, NON_SCHENGEN_LOUNGES } from '@fcom/booking/utils/common-data.utils';

/**
 * Describes where the modal text content comes from for each bundle category:
 * - urlKey: a static CMS localization key (resolved at runtime via | finLocalization)
 * - moreAbout: per-variant CMS article at ancillaries.{cat}.{variant}.moreAbout.article.url
 * - lounge: CMS fragment chosen by variant, same schengen/non-schengen split as the standalone lounge flow
 * - translation: already in ServiceCatalogService.translations (no CMS call needed)
 */
export type ContentSource =
  | { type: 'urlKey'; key: string }
  | { type: 'moreAbout' }
  | { type: 'meal' }
  | { type: 'lounge' }
  | { type: 'translation'; field: 'ingredients' | 'description' };

export interface ModalContentSources {
  content?: ContentSource;
  descriptionText?: ContentSource;
  notificationKey?: string;
}

/**
 * Describes where the product-info modal text content comes from, keyed by the *individual ancillary*
 * category (not the bundle category). This is the single source of truth used both by the seat bundle
 * modal (via `BUNDLE_CATEGORY_ANCILLARY_MAP`) and by the ancillary+ancillary combo service cards,
 * where each card opens a modal for one specific service. A category with no entry (e.g. wifi) has no
 * modal — there is too little to show to warrant one.
 */
export const ANCILLARY_MODAL_CONTENT: Partial<Record<Category, ModalContentSources>> = {
  [Category.PRIORITY]: {
    content: { type: 'urlKey', key: 'fragments.helsinkiPriorityContent.url' },
    descriptionText: { type: 'translation', field: 'description' },
    notificationKey: 'ancillaries.priority.additionalInfoBlockNotificationHel',
  },
  [Category.MEAL]: {
    content: { type: 'translation', field: 'ingredients' },
    descriptionText: { type: 'translation', field: 'description' },
  },
  [Category.CABIN_BAGGAGE]: {
    content: { type: 'moreAbout' },
    descriptionText: { type: 'translation', field: 'description' },
  },
  [Category.LOUNGE]: {
    content: { type: 'lounge' },
    descriptionText: { type: 'translation', field: 'description' },
  },
};

/**
 * Maps a seat-bundle category to the individual ancillary category whose standalone catalog content the
 * product-info modal borrows (e.g. SEAT_MEAL → MEAL). Used by `bundleProductInfoSelector` to resolve which
 * service to show; the content itself then comes from `ANCILLARY_MODAL_CONTENT` keyed by the resolved
 * ancillary category. A bundle category with no entry has no product-info modal.
 */
export const BUNDLE_CATEGORY_ANCILLARY_MAP: Partial<Record<Category, Category>> = {
  [Category.SEAT_PRIORITY]: Category.PRIORITY,
  [Category.SEAT_MEAL]: Category.MEAL,
  [Category.SEAT_CABIN_BAGGAGE]: Category.CABIN_BAGGAGE,
};

/** Whether an individual ancillary category has product-info modal content defined. */
export const hasProductInfoModal = (ancillaryCategory: Category): boolean =>
  !!ANCILLARY_MODAL_CONTENT[ancillaryCategory];

/** CMS localization key for a single ancillary's modal content, or null when it comes from DAPI translations. */
export function getServiceContentUrlKey(ancillaryCategory: Category, service: ServiceCatalogService): string | null {
  const content = ANCILLARY_MODAL_CONTENT[ancillaryCategory]?.content;
  if (content?.type === 'urlKey') return content.key;
  if (content?.type === 'moreAbout') return `ancillaries.${service.category}.${service.variant}.moreAbout.article.url`;
  if (content?.type === 'lounge') return getLoungeContentFragmentKey(NON_SCHENGEN_LOUNGES.includes(service.variant));
  return null;
}

/** HTML for a single ancillary already available from DAPI translations, or null when a CMS fetch is used instead. */
export function getServiceContentHtml(ancillaryCategory: Category, service: ServiceCatalogService): string | null {
  const content = ANCILLARY_MODAL_CONTENT[ancillaryCategory]?.content;
  if (content?.type === 'translation') return service.translations?.[content.field] ?? null;
  return null;
}

/** Short description for a single ancillary, sourced from DAPI translations, or null when not defined. */
export function getServiceDescriptionText(ancillaryCategory: Category, service: ServiceCatalogService): string | null {
  const descriptionText = ANCILLARY_MODAL_CONTENT[ancillaryCategory]?.descriptionText;
  if (descriptionText?.type === 'translation') return service.translations?.[descriptionText.field] ?? null;
  return null;
}

/** i18n key for a single ancillary's modal notification, or null when none is defined. */
export function getServiceNotificationKey(ancillaryCategory: Category): string | null {
  return ANCILLARY_MODAL_CONTENT[ancillaryCategory]?.notificationKey ?? null;
}
